import { AppDispatch } from '../store';
import { 
  saveTripDraft, 
  updateTripDraft, 
  deleteTripDraft, 
  clearAllTripDrafts, 
  setTripDrafts, 
  TripDraft 
} from '../store/slices/TripDraftSlice';
import { fileToBase64WithMime } from './base64Converter';

// Redux-based trip draft utility functions
export const tripDraftUtils = {
  saveDraft: (dispatch: AppDispatch, draftData: Omit<TripDraft, 'id' | 'createdAt' | 'updatedAt'>) => {
    dispatch(saveTripDraft(draftData));
  },

  updateDraft: (dispatch: AppDispatch, id: string, updatedDraft: Partial<TripDraft>) => {
    dispatch(updateTripDraft({ id, draft: updatedDraft }));
  },

  deleteDraft: (dispatch: AppDispatch, id: string) => {
    dispatch(deleteTripDraft(id));
  },

  clearAllDrafts: (dispatch: AppDispatch) => {
    dispatch(clearAllTripDrafts());
  },

  setDrafts: (dispatch: AppDispatch, drafts: TripDraft[]) => {
    dispatch(setTripDrafts(drafts));
  },

  // Enhanced save draft with base64 conversion for media files
  saveDraftWithBase64: async (
    dispatch: AppDispatch, 
    draftData: Omit<TripDraft, 'id' | 'createdAt' | 'updatedAt' | 'imagesData' | 'videosData'>,
    mediaFiles?: {
      images?: File[];
      videos?: File[];
    }
  ) => {
    let imagesData: any[] = [];
    let videosData: any[] = [];

    // Convert images to base64
    if (mediaFiles?.images && mediaFiles.images.length > 0) {
      imagesData = await Promise.all(
        mediaFiles.images.map(async (file) => {
          const base64 = await fileToBase64WithMime(file, true);
          return {
            base64,
            name: file.name,
            type: file.type,
            size: file.size,
            lastModified: file.lastModified,
          };
        })
      );
    }

    // Convert videos to base64
    if (mediaFiles?.videos && mediaFiles.videos.length > 0) {
      videosData = await Promise.all(
        mediaFiles.videos.map(async (file) => {
          const base64 = await fileToBase64WithMime(file, true);
          return {
            base64,
            name: file.name,
            type: file.type,
            size: file.size,
            lastModified: file.lastModified,
          };
        })
      );
    }

    const draftWithMedia = {
      ...draftData,
      imagesData,
      videosData,
    };

    dispatch(saveTripDraft(draftWithMedia));
  },

  // Enhanced update draft with base64 conversion for media files
  updateDraftWithBase64: async (
    dispatch: AppDispatch,
    id: string,
    updatedDraft: Partial<Omit<TripDraft, 'id' | 'createdAt' | 'updatedAt' | 'imagesData' | 'videosData'>>,
    mediaFiles?: {
      images?: File[];
      videos?: File[];
    }
  ) => {
    let imagesData: any[] = [];
    let videosData: any[] = [];

    // Convert images to base64
    if (mediaFiles?.images && mediaFiles.images.length > 0) {
      imagesData = await Promise.all(
        mediaFiles.images.map(async (file) => {
          const base64 = await fileToBase64WithMime(file, true);
          return {
            base64,
            name: file.name,
            type: file.type,
            size: file.size,
            lastModified: file.lastModified,
          };
        })
      );
    }

    // Convert videos to base64
    if (mediaFiles?.videos && mediaFiles.videos.length > 0) {
      videosData = await Promise.all(
        mediaFiles.videos.map(async (file) => {
          const base64 = await fileToBase64WithMime(file, true);
          return {
            base64,
            name: file.name,
            type: file.type,
            size: file.size,
            lastModified: file.lastModified,
          };
        })
      );
    }

    const draftWithMedia = {
      ...updatedDraft,
      ...(imagesData.length > 0 && { imagesData }),
      ...(videosData.length > 0 && { videosData }),
    };

    dispatch(updateTripDraft({ id, draft: draftWithMedia }));
  },
};

// Re-export the TripDraft type for convenience
export type { TripDraft };
